'use client';

import { Card, CardContent, Spinner } from '@lectura/ui';
import { Library } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/lib/auth';
import { useCollections } from '@/lib/queries';
import { useHydrated } from '@/lib/use-hydrated';

export interface FeaturedCollectionsProps {
  /** Hány kollekciót mutasson — alapból 6. */
  limit?: number;
}

/**
 * A kezdőlap „Tematikus kollekciók" szekciója — a `catalog-svc` admin-oldalán
 * létrehozott kollekciókat (sorozatok, témakörök) listázza a bejelentkezett
 * felhasználónak. A `catalog-svc` `/collections` végpontja `TenantScope`-os
 * (Bearer-token kötelező), ezért bejelentkezetlen renderben a szekció rejtve
 * marad — így nincs 401-zaj a Network tab-on.
 *
 * Ha a tenant még nem hozott létre kollekciót, az egész szekció szintén rejtve
 * marad (nem mutatunk üres-state-et, hogy a kezdőlap maradjon kompakt). Ha a
 * backend hibázik, szintén nem rontjuk a kezdőlap-élményt — csendben kihagyjuk.
 */
export function FeaturedCollections({ limit = 6 }: FeaturedCollectionsProps) {
  const hydrated = useHydrated();
  const isLogged = useAuth((s) => s.isAuthenticated());
  const { data, isLoading, isError } = useCollections();

  if (!hydrated || !isLogged) return null;

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (isError) return null;
  const items = (data ?? []).slice(0, limit);
  if (items.length === 0) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((c) => (
        <Link
          key={c.id}
          href={`/collections/${c.id}` as const}
          aria-label={`Kollekció: ${c.name}`}
        >
          <Card className="h-full transition-shadow hover:shadow-card-hover">
            <CardContent className="flex h-full flex-col gap-2 p-5">
              <div className="flex items-center gap-2">
                <span className="flex size-9 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                  <Library className="size-4" />
                </span>
                <h3 className="font-display text-base font-semibold leading-tight">
                  {c.name}
                </h3>
              </div>
              {c.description ? (
                <p className="line-clamp-2 text-sm text-[var(--color-foreground-muted)]">
                  {c.description}
                </p>
              ) : null}
              <p className="mt-auto text-xs font-medium text-brand-600">
                {c.itemCount} kiadvány
              </p>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
